import { useState, type ReactNode } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import type { TournamentStatus } from '../domain';
import { isSuperAdmin } from '../domain';
import { useTournament } from '../state/TournamentContext';
import { useAuth } from '../state/AuthContext';
import { useTranslation } from '../state/I18nContext';
import { BrandLogo } from './BrandLogo';
import { DeleteTournamentDialog } from './DeleteTournamentDialog';
import { ShareTournamentDialog } from './ShareTournamentDialog';
import { AccountMenu } from './AccountMenu';
import { LanguageSelector } from './LanguageSelector';

export type ViewKey =
  | 'players'
  | 'groups'
  | 'ranks'
  | 'bracket'
  | 'registrations'
  | 'photos';

/** Top-level destinations shown in the app menu when no tournament is open. */
export type SectionKey = 'tournaments' | 'companies' | 'users' | 'messaging';

interface LayoutProps {
  /** Whether a tournament is currently open (detail mode). */
  detail: boolean;
  view: ViewKey;
  onChange: (view: ViewKey) => void;
  /** Active top-level section (shown when no tournament is open). */
  section: SectionKey;
  onSectionChange: (section: SectionKey) => void;
  /**
   * Open the athlete surface (see `features/athlete-access.md`). Present only for
   * a staff account that also holds an athlete profile of its own — a pure
   * athlete account never sees the admin layout at all.
   */
  onMyProfile?: () => void;
  children: ReactNode;
}

const STATUS_COLOR: Record<TournamentStatus, 'default' | 'info' | 'success'> = {
  DRAFT: 'default',
  IN_PROGRESS: 'info',
  FINISHED: 'success',
};

export function Layout({
  detail,
  view,
  onChange,
  section,
  onSectionChange,
  onMyProfile,
  children,
}: LayoutProps) {
  const { tournament, close, remove } = useTournament();
  const { me, hasPermission } = useAuth();
  const { t } = useTranslation();
  // The Users section opens to anyone holding a user-management permission
  // (mirrors `App.tsx`); Messaging stays Super-Admin-only. The server still
  // enforces its own permission checks on every request.
  const superAdmin = me !== null && isSuperAdmin(me.user);
  const userManager =
    hasPermission('CREATE_USER') ||
    hasPermission('EDIT_USER') ||
    hasPermission('DELETE_USER');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const status: TournamentStatus = tournament?.status ?? 'DRAFT';
  // The group stage / bracket only exist once the tournament has started.
  const started = status === 'IN_PROGRESS' || status === 'FINISHED';

  const confirmDelete = () => {
    setDeleteOpen(false);
    void remove();
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="sticky" elevation={0}>
        {/* Row 1 — branding, tournament context and actions. Kept free of the
            navigation tabs so nothing collides or clips on narrower desktops. */}
        <Toolbar sx={{ gap: { xs: 1, sm: 2 } }}>
          <BrandLogo />
          <Typography variant="h6" noWrap sx={{ flexShrink: 0 }}>
            {t('admin.layout.brand')}
          </Typography>

          {detail && tournament ? (
            <>
              <Button
                color="inherit"
                startIcon={<ArrowBackIcon />}
                onClick={close}
                sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
              >
                {t('admin.layout.allTournaments')}
              </Button>
              <Typography
                variant="subtitle1"
                noWrap
                sx={{ minWidth: 0, flexShrink: 1 }}
                title={tournament.name}
              >
                {tournament.name}
              </Typography>
              <Chip
                size="small"
                color={STATUS_COLOR[status]}
                label={t(`admin.common.status.${status}`)}
                sx={{ flexShrink: 0 }}
              />
              <Box sx={{ flexGrow: 1 }} />
              <Button
                color="inherit"
                startIcon={<QrCode2Icon />}
                onClick={() => setShareOpen(true)}
                sx={{ flexShrink: 0 }}
              >
                {t('admin.layout.share')}
              </Button>
              {hasPermission('DELETE_TOURNAMENT') && (
                <Button
                  color="inherit"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => setDeleteOpen(true)}
                  sx={{ flexShrink: 0 }}
                >
                  {t('admin.common.delete')}
                </Button>
              )}
            </>
          ) : (
            <Box sx={{ flexGrow: 1 }} />
          )}
          <LanguageSelector />
          {/* Everything about the signed-in account — profile, settings, sign
              out — lives behind one avatar, keeping this row free for the
              tournament context and its actions. */}
          <AccountMenu onMyProfile={onMyProfile} />
        </Toolbar>

        {/* Row 2 — full-width navigation tabs. Scrollable as a safety net so no
            tab is ever hidden, regardless of tab count or viewport width.
            Inside a tournament these are the tournament views; otherwise the
            top-level app menu (Tournaments / Companies / Users) lives in the
            same place. */}
        {detail && tournament ? (
          <Box sx={{ borderTop: 1, borderColor: 'divider', px: { xs: 1, sm: 2 } }}>
            <Tabs
              value={view}
              onChange={(_, value: ViewKey) => onChange(value)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab
                value="groups"
                label={t('admin.layout.tabs.groupStage')}
                disabled={!started}
              />
              <Tab
                value="bracket"
                label={t('tabs.bracket')}
                disabled={!started}
              />
              <Tab value="ranks" label={t('tabs.ranks')} disabled={!started} />
              <Tab value="players" label={t('admin.layout.tabs.players')} />
              <Tab value="photos" label={t('tabs.photos')} />
              {tournament.acceptPublicRegistration && (
                <Tab
                  value="registrations"
                  label={t('admin.layout.tabs.registrations')}
                />
              )}
            </Tabs>
          </Box>
        ) : (
          <Box sx={{ borderTop: 1, borderColor: 'divider', px: { xs: 1, sm: 2 } }}>
            <Tabs
              value={section}
              onChange={(_, value: SectionKey) => onSectionChange(value)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab
                value="tournaments"
                label={t('admin.layout.tabs.tournaments')}
              />
              <Tab value="companies" label={t('admin.layout.tabs.companies')} />
              {userManager && (
                <Tab value="users" label={t('admin.layout.tabs.users')} />
              )}
              {superAdmin && (
                <Tab value="messaging" label={t('admin.layout.tabs.messaging')} />
              )}
            </Tabs>
          </Box>
        )}
      </AppBar>

      <Container maxWidth={false} sx={{ py: 3, flexGrow: 1 }}>
        {children}
      </Container>

      <DeleteTournamentDialog
        open={deleteOpen}
        status={status}
        onClose={() => setDeleteOpen(false)}
        onConfirm={confirmDelete}
      />

      {tournament && (
        <ShareTournamentDialog
          tournamentId={tournament.id}
          tournamentName={tournament.name}
          open={shareOpen}
          onClose={() => setShareOpen(false)}
        />
      )}
    </Box>
  );
}
